/**
 * @file WmsDocumentMovements.tsx
 * @description Адаптер «Движения по документу» для складских документов
 * (phase19.23 этап A, пункт 10 чек-листа A6).
 *
 * Отвечает на вопрос кладовщика «что этот документ сделал с остатком».
 *
 * Запрос идёт серверным фильтром по документу (`refReceiptId` /
 * `refWriteoffId`, phase19.23): окно по дате не ставим намеренно — документ
 * обязан показать ВСЕ свои движения, а не те, что попали в последние сутки.
 * Клиентская доводка по тому же полю оставлена как страховка: старый сервер
 * молча игнорирует незнакомый параметр, и без неё в карточку приехали бы чужие
 * строки журнала.
 *
 * Разметка — в домен-нейтральном `DocumentHistoryPanel`.
 */

import { useEffect, useMemo, useState } from "react";

import { DocumentHistoryPanel, type DocumentHistoryRow } from "../ui/DocumentHistoryPanel";
import {
  MOVEMENT_KIND_LABEL,
  formatSkuName,
  stockApi,
  type StockMovementDto,
} from "../../lib/api-wms";
import { wmsErrorText } from "../../lib/wms-error-text";

/** Вид документа — от него зависит поле ссылки в движении. */
export type WmsDocumentRef = "receipt" | "writeoff" | "transfer";

/**
 * Вид документа → поле движения. Реестр, а не ветка `if`: четвёртый документ
 * (инвентаризация) добавляется записью.
 */
const REF_FIELD: Record<
  WmsDocumentRef,
  "refReceiptId" | "refWriteoffId" | "refTransferId"
> = {
  receipt: "refReceiptId",
  writeoff: "refWriteoffId",
  transfer: "refTransferId",
};

/** Потолок выборки: больше строк на один документ — повод открыть журнал. */
const MOVEMENTS_LIMIT = 500;

interface Props {
  documentId: string;
  documentKind: WmsDocumentRef;
  /** Склад документа — сужает выборку журнала на старом сервере. */
  warehouseId?: string;
}

export function WmsDocumentMovements({
  documentId,
  documentKind,
  warehouseId,
}: Props) {
  const [items, setItems] = useState<StockMovementDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    stockApi
      .movements({
        warehouseId: warehouseId || undefined,
        [REF_FIELD[documentKind]]: documentId,
        limit: MOVEMENTS_LIMIT,
      })
      .then((data) => {
        if (cancelled) return;
        setItems(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(wmsErrorText(err, "Не удалось загрузить движения"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [documentId, documentKind, warehouseId]);

  const rows = useMemo<DocumentHistoryRow[]>(() => {
    const field = REF_FIELD[documentKind];
    return items
      .filter((m) => m[field] === documentId)
      .map((m) => ({
        id: m.id,
        when: new Date(m.createdAt).toLocaleString("ru-RU"),
        kind: MOVEMENT_KIND_LABEL[m.kind],
        title: formatSkuName(m.sku),
        subtitle: `${m.skuCode}${m.lot ? ` · партия ${m.lot.batchNumber}` : ""}`,
        amount: m.quantity,
        actor: m.actor?.displayName ?? null,
      }));
  }, [items, documentId, documentKind]);

  return (
    <DocumentHistoryPanel
      title="Движения по документу"
      rows={rows}
      loading={loading}
      error={error}
      emptyText="Движения по документу не найдены"
      // Выборка упёрлась в потолок — часть строк осталась за кадром.
      note={
        items.length >= MOVEMENTS_LIMIT
          ? "Показаны не все движения: выборка ограничена. Полный список — в журнале движений склада."
          : null
      }
    />
  );
}
